import { FC } from 'react'
import { useParams } from 'react-router-dom'
import { SentimentDistributionCard } from 'src/components/SentimentDistributionCard'
import {
  ReviewStatsCards,
  StatCard,
} from 'src/pages/ReviewAnalysis/BrandLevelWidgets/ReviewStatsCards'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { ScopeOption, ScopeSelect } from 'src/pages/ReviewAnalysis/ScopeSelect'
import { useGetReviewAnalysisPlatformActivityQuery } from 'src/reactQuery/useGetReviewAnalysisPlatformActivityQuery'
import { useGetReviewAnalysisPlatformAnalysisQuery } from 'src/reactQuery/useGetReviewAnalysisPlatformAnalysisQuery'
import {
  useReviewAnalysisDateRange,
  useReviewAnalysisPeriodLabel,
} from 'src/hooks/useReviewAnalysisDateRange'
import { LocationComparison } from './LocationComparison'
import { PerformanceLocations } from './PerformanceLocations'
import { RatingAnalytics } from './RatingAnalytics'
import { ReviewVelocity } from './ReviewVelocity'
import styles from './platformReviewAnalysis.module.scss'

interface PlatformReviewAnalysisProps {
  // The facilities picked in the header's "All Facilities" filter ([] = all).
  // Owned by the page, which also scopes the reviews list at its bottom.
  facilityIds: string[]
  onFacilitiesChange: (facilityIds: string[]) => void
}

export const PlatformReviewAnalysis: FC<PlatformReviewAnalysisProps> = ({
  facilityIds,
  onFacilitiesChange,
}) => {
  const { campaignId = '', platformId = 'tripadvisor' } = useParams<{
    campaignId: string
    platformId: string
  }>()

  const dateRange = useReviewAnalysisDateRange()
  const periodLabel = useReviewAnalysisPeriodLabel()
  const { data } = useGetReviewAnalysisPlatformAnalysisQuery(
    campaignId,
    platformId,
    facilityIds,
    dateRange
  )

  // Velocity, spike and alerts for the Review Velocity & Dynamics section,
  // for the platform narrowed to the picked facilities.
  const { data: activity, isFetching: isActivityFetching } =
    useGetReviewAnalysisPlatformActivityQuery(
      campaignId,
      platformId,
      dateRange,
      true,
      facilityIds
    )

  if (!data) return <div className={styles.container} />

  const facilities: ScopeOption[] = data.facilityTabs
    .filter((tab) => !!tab.id)
    .map((tab) => ({ id: tab.id as string, name: tab.name }))
  const PlatformIcon = getPlatformIcon(data.platform)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.platformIcon}>
            <PlatformIcon />
          </div>
          <h2 className={styles.title}>
            Platform Review Analysis: {data.platformName}
          </h2>
        </div>
        {/* The design's "All Facilities" filter in the right corner: a row of
            one tab per facility did not fit (QA, 2026-10-13). */}
        {facilities.length > 1 && (
          <ScopeSelect
            allLabel="All Facilities"
            noun="facilities"
            onChange={onFacilitiesChange}
            options={facilities}
            selected={facilityIds}
          />
        )}
      </div>

      {/* `stats` is null whenever the scoped set is empty — the backend sends
          `facScoped.length > 0 ? statsBlock(...) : null`. Selecting a facility
          tab with no reviews for this platform in the active window used to
          dereference null here and take the whole section down. `ratingSummary`
          below was already guarded with `?.`; this row was missed. */}
      {data.stats && (
        <div className={styles.statsRow}>
          <ReviewStatsCards
            averageRating={data.stats.averageRating}
            averageRatingChange={data.stats.averageRatingChange}
            combined
            periodLabel={periodLabel}
            totalReviews={data.stats.totalReviews}
            totalReviewsChange={data.stats.totalReviewsChange}
            trendLabel={dateRange ? 'vs previous period' : undefined}
          />
          <StatCard
            change={data.stats.positiveRateChange}
            subtitle={`${data.locations.length} ${
              data.locations.length === 1 ? 'Location' : 'Locations'
            }`}
            title="Positive Review Rate"
            trendLabel={dateRange ? 'vs previous period' : undefined}
            value={`${data.stats.sentiment.positive.percentage}%`}
            valueCaption="of all reviews are positive"
          />
          <SentimentDistributionCard data={data.stats.sentiment} />
        </div>
      )}

      <PerformanceLocations
        bestLocations={data.performance.best}
        worstLocations={data.performance.worst}
      />

      <LocationComparison locations={data.locations} />

      <RatingAnalytics
        ProviderIcon={PlatformIcon}
        averageRating={data.ratingSummary?.averageRating}
        averageRatingChangePercent={data.ratingSummary?.trendPercent}
        averageRatingDelta={data.ratingSummary?.ratingDelta}
        distribution={data.distribution}
        mismatchCount={data.ratingSummary?.mismatch?.negativeTextReviews}
        mismatchPercentage={data.ratingSummary?.mismatch?.percentage}
        mismatchReviews={data.mismatchReviews}
        providerName={data.platformName}
        trend={data.trend}
      />

      {/* Mounted while the activity query is in flight so the section skeletons
          in place instead of appearing late and pushing the page down. */}
      {(activity || isActivityFetching) && (
        <ReviewVelocity
          ProviderIcon={PlatformIcon}
          alerts={activity?.alerts}
          isLoading={isActivityFetching}
          platformActivity={activity?.platformActivity}
          providerName={data.platformName}
          spike={activity?.spike}
          velocity={activity?.velocity}
        />
      )}
    </div>
  )
}
